import { useAuthStore } from '@/stores/authStore'
import { useWebSocket } from '@/composable/useWebsockets'
import { computed, onUnmounted, ref, type Ref } from 'vue'
import { useMyToast } from '@/composable/useMyToast'
import { i18n } from '@/i18n'
import type { MarketType, WsOrderBookResponse, WsSubRequest, WsSubResponse } from '@/utils/types'
import type { CexOhlcvResponse } from '@/services/dex-pools-v2-service/types'
import { useOrderBookStore } from '@/stores/orderbooksStore'
import { calcPriceLevel, getBuyMarketType, getSellMarketType } from '@/utils'
import { isNull, throttle } from 'lodash'
import { ORDERBOOK_TIMEOUT } from '@/utils/consts'
import { useAutoRefresh } from '@/composable/useAutorefresh'
import { useSpreadBellStore } from '@/stores/spreadBellStore'
import bellSoundFile from '@/assets/sounds/alert.wav'
import { useSound } from '@vueuse/sound'
import { useFlasherStore } from '@/stores/flasherStore'

type OrderBookSide = 'asks' | 'bids'

export const useCexCexSpread = (
  exBuy: Ref<string>,
  exSell: Ref<string>,
  marketType: Ref<MarketType>,
  asset: Ref<string>,
  sellAsset: Ref<string>,
  orderUsdtAmount: Ref<number>,
  onOhlcv: (
    exchange: string,
    marketType: MarketType,
    candle: CexOhlcvResponse,
    timestamp: number,
  ) => void,
  quoteBuy: Ref<string>,
  quoteSell: Ref<string>,
) => {
  const { t } = i18n.global
  const { addToast } = useMyToast()
  const authStore = useAuthStore()
  const bellStore = useSpreadBellStore()
  const bellSound = useSound(bellSoundFile)
  const flasherStore = useFlasherStore()

  const orderBookStore = useOrderBookStore()
  const wsAttempts = ref(0)
  const restartingByLatency = ref(false)

  // Throttled batch state
  const pendingObUpdates = new Map<
    string,
    { exchange: string; marketType: MarketType; asset: string; ob: WsOrderBookResponse }
  >()
  const pendingOhlcvUpdates = new Map<
    string,
    { exchange: string; marketType: MarketType; candle: CexOhlcvResponse; ts: number }
  >()

  // Memoization cache
  const obPriceCache = new Map<string, [number, number] | null>()

  const priceBuyLoading = computed(() => {
    return !orderBookStore.exists(exBuy.value, buyMarketType.value, asset.value)
  })
  const priceSellLoading = computed(
    () => !orderBookStore.exists(exSell.value, sellMarketType.value, sellAsset.value),
  )

  // Вычисляем ценовой уровень опираясь на объём
  const calcPriceVol = (
    exchange: string,
    asset: string,
    marketType: MarketType,
    side: OrderBookSide,
    amount: number,
  ) => {
    const cacheKey = `${exchange}/${marketType}/${asset}|${side}|${amount}`.toLowerCase()
    const cached = obPriceCache.get(cacheKey)
    if (cached !== undefined) return cached
    const book = orderBookStore.get(exchange, marketType, asset)
    if (!book) {
      obPriceCache.set(cacheKey, null)
      return null
    }
    const sided = book[side]
    const priceVol = calcPriceLevel(sided, amount)
    if (priceVol && priceVol.length == 2) {
      obPriceCache.set(cacheKey, priceVol)
      return priceVol
    }
    obPriceCache.set(cacheKey, null)
    return null
  }

  const buyMarketType = computed(() => getBuyMarketType(marketType.value))
  const sellMarketType = computed(() => getSellMarketType(marketType.value))

  const priceVolBuy = computed(() =>
    calcPriceVol(exBuy.value, asset.value, buyMarketType.value, 'asks', orderUsdtAmount.value),
  )
  const priceVolSell = computed(() =>
    calcPriceVol(exSell.value, sellAsset.value, sellMarketType.value, 'bids', orderUsdtAmount.value),
  )
  const backPriceVolSell = computed(() =>
    calcPriceVol(exBuy.value, asset.value, buyMarketType.value, 'bids', orderUsdtAmount.value),
  )
  const backPriceVolBuy = computed(() =>
    calcPriceVol(exSell.value, sellAsset.value, sellMarketType.value, 'asks', orderUsdtAmount.value),
  )

  // Проверяем факт отсутствия объёмов в ценах из которых затем считается спред
  const isLowVolBuy = computed(
    () =>
      orderBookStore.exists(exBuy.value, buyMarketType.value, asset.value) && !priceVolBuy.value,
  )
  const isLowVolSell = computed(
    () =>
      orderBookStore.exists(exSell.value, sellMarketType.value, sellAsset.value) && !priceVolSell.value,
  )
  const isSpreadOk = computed(() => !isLowVolBuy.value && !isLowVolSell.value)

  // Проверяем факт отсутствия объёмов в ценах из которых затем считается обратный спред
  const isLowBackVolBuy = computed(
    () =>
      orderBookStore.exists(exBuy.value, buyMarketType.value, asset.value) &&
      !backPriceVolSell.value,
  )
  const isLowBackVolSell = computed(
    () =>
      orderBookStore.exists(exSell.value, sellMarketType.value, sellAsset.value) &&
      !backPriceVolBuy.value,
  )
  const isBackSpreadOk = computed(() => !isLowBackVolBuy.value && !isLowBackVolSell.value)

  const priceBuy = computed(() => priceVolBuy.value?.[0] ?? null)
  const priceSell = computed(() => priceVolSell.value?.[0] ?? null)
  const backPriceBuy = computed(() => backPriceVolBuy.value?.[0] ?? null)
  const backPriceSell = computed(() => backPriceVolSell.value?.[0] ?? null)
  const volBuy = computed(() => priceVolBuy.value?.[1] ?? null)
  const volSell = computed(() => priceVolSell.value?.[1] ?? null)
  const spread = computed(() =>
    priceBuy.value && priceSell.value ? (priceSell.value / priceBuy.value - 1) * 100 : null,
  )
  const backSpread = computed(() =>
    backPriceSell.value && backPriceBuy.value
      ? (1 - backPriceBuy.value / backPriceSell.value) * 100
      : null,
  )

  const flushObUpdates = throttle(
    () => {
      if (pendingObUpdates.size === 0) return
      for (const { exchange, marketType, asset, ob } of pendingObUpdates.values()) {
        orderBookStore.set(exchange, marketType, asset, ob)
      }
      // invalidate cached price levels for updated orderbooks
      for (const { exchange, marketType, asset } of pendingObUpdates.values()) {
        const base = `${exchange}/${marketType}/${asset}`.toLowerCase()
        obPriceCache.delete(`${base}|asks|${orderUsdtAmount.value}`)
        obPriceCache.delete(`${base}|bids|${orderUsdtAmount.value}`)
      }
      pendingObUpdates.clear()
    },
    500,
    { leading: true, trailing: true },
  )

  const flushOhlcvUpdates = throttle(
    () => {
      if (pendingOhlcvUpdates.size === 0) return
      for (const { exchange, marketType, candle, ts } of pendingOhlcvUpdates.values()) {
        onOhlcv(exchange, marketType, candle, ts)
      }
      pendingOhlcvUpdates.clear()
    },
    500,
    { leading: true, trailing: true },
  )

  const onClose = (reconnect: () => void) => {
    wsAttempts.value += 1
    if (wsAttempts.value % 5 == 0) {
      addToast({ severity: 'error', message: t(`alerts.connectionLost`) })
    }
    window.setTimeout(() => {
      reconnect()
    }, 1200)
  }

  const onOpen = () => {
    restartingByLatency.value = false
    wsAttempts.value = 0
  }

  const onMessage = async (message: string) => {
    const response: WsSubResponse = JSON.parse(message)
    if (response.event == 'watch') {
      const params = response.params
      if (!params) {
        return
      }
      if (params[0] == 'ob') {
        const marketType = params[1] as MarketType
        const exchange = params[2]
        if (!exchange || !marketType) {
          return
        }
        const data = response.data as WsOrderBookResponse
        const obAsset = exchange === exSell.value ? sellAsset.value : asset.value
        pendingObUpdates.set(`${exchange}/${marketType}/${obAsset}`.toLowerCase(), {
          exchange,
          marketType,
          asset: obAsset,
          ob: data,
        })
        flushObUpdates()
      } else if (params[0] == 'last-ohlcv') {
        const data = response.data as CexOhlcvResponse[]
        if (!data[0] || !response.ts) {
          return
        }
        const exchange = params[1] as string
        const marketType = params[2] as MarketType
        pendingOhlcvUpdates.set(`${exchange}/${marketType}`.toLowerCase(), {
          exchange,
          marketType,
          candle: data[0],
          ts: response.ts,
        })
        flushOhlcvUpdates()
      }
    }
  }

  const { run, stop } = useWebSocket(
    import.meta.env.VITE_WS_URL + '/api/v2/ws?token=' + authStore.token,
    {
      onMessage: onMessage,
      handleClose: onClose,
      handleOpen: onOpen,
    },
  )

  const checkOrderbookLatency = () => {
    const lastObTsBuy =
      orderBookStore.get(exBuy.value, buyMarketType.value, asset.value)?.timestamp ?? null
    const lastObTsSell =
      orderBookStore.get(exSell.value, sellMarketType.value, sellAsset.value)?.timestamp ?? null

    if (isNull(lastObTsBuy) || isNull(lastObTsSell)) {
      return
    }

    const timestamp = +new Date()
    const deltaBuy = timestamp - lastObTsBuy
    const deltaSell = timestamp - lastObTsSell

    if (deltaBuy > ORDERBOOK_TIMEOUT || deltaSell > ORDERBOOK_TIMEOUT) {
      console.error(
        `deltaBuy=${deltaBuy}; timestamp=${timestamp}; deltaSell=${deltaSell}; lastObTsBuy=${lastObTsBuy}; lastObTsSell=${lastObTsSell}; reconnecting...`,
      )
      restartingByLatency.value = true
      stop()
    }
  }

  const checkOrderbookLatencyRefresher = useAutoRefresh(async () => {
    checkOrderbookLatency()
  }, 1000)

  onUnmounted(() => {
    checkOrderbookLatencyRefresher.stopRefresh()
  })

  const startWebSocket = () => {
    if (
      !buyMarketType.value ||
      !sellMarketType.value ||
      !exBuy.value ||
      !exSell.value ||
      !asset.value ||
      !sellAsset.value
    ) {
      return
    }

    // Reset pending batches and cache on new session
    pendingObUpdates.clear()
    pendingOhlcvUpdates.clear()
    obPriceCache.clear()

    const subMessages: Array<WsSubRequest> = [
      {
        action: 'watch',
        params: ['ob', buyMarketType.value, exBuy.value, asset.value, quoteBuy.value],
      },
      {
        action: 'watch',
        params: ['ob', sellMarketType.value, exSell.value, sellAsset.value, quoteSell.value],
      },
      {
        action: 'watch',
        params: ['last-ohlcv', exBuy.value, buyMarketType.value, asset.value, quoteBuy.value],
      },
      {
        action: 'watch',
        params: ['last-ohlcv', exSell.value, sellMarketType.value, sellAsset.value, quoteSell.value],
      },
    ]
    const msg = JSON.stringify(subMessages)
    run(msg)
  }

  const checkBell = (v: number | null, source: number | null) => {
    if (v && source && v >= source) {
      bellSound.play()
      flasherStore.start()
    }
  }

  const checkBellRefresher = useAutoRefresh(() => {
    checkBell(spread.value, bellStore.spread)
    checkBell(backSpread.value, bellStore.backSpread)
  }, 1000)

  return {
    stopWebSocket: stop,
    startWebSocket,
    checkOrderbookLatency,
    checkBellRefresher,
    buyMarketType,
    sellMarketType,
    checkOrderbookLatencyRefresher,
    priceBuyLoading,
    priceSellLoading,
    isSpreadOk,
    isBackSpreadOk,
    priceBuy,
    priceSell,
    backPriceBuy,
    backPriceSell,
    volBuy,
    volSell,
    spread,
    backSpread,
    isLowVolBuy,
    isLowVolSell,
    isLowBackVolBuy,
    restartingByLatency,
    isLowBackVolSell,
  }
}
